import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X, Plus, Music, Loader2, Mic, Check, ChevronRight, Flame, ArrowLeft, Bookmark, Play, Radio } from 'lucide-react';
import { debounce } from 'lodash';
import { useMusicKit } from '../../context/MusicKitContext';
import { useTheme } from '../../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Apple Music Genre IDs with glowing icon images and accent colors
// Order: Pop, Hip-Hop, R&B, Latin, Electronic, Rock, Jazz, K-Pop, Country, Reggae, Classical (Afrobeats REMOVED)
// Note: Reggae now uses the Afrobeats icon (djembe drums)
const GENRES = [
  { id: 14, name: 'Pop', accent: 'purple', image: 'https://static.prod-images.emergentagent.com/jobs/db348711-c33f-411a-ab4f-4b2b6c5b3e49/images/feaa84d867ffe9c9fc252df255457b08e45d9ca37e95e37fa4c8db4c037783cd.png' },
  { id: 18, name: 'Hip-Hop', accent: 'orange', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/aa7f0ed8f0fa64fa2d73180fe4b815515d9c21ed94a825b83304d04c0b92d2be.png' },
  { id: 15, name: 'R&B', accent: 'pink', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/acf386e9b9e27fd9e8c5eacb7cdbf5fd68d4e659479de72110c40ad83ae56cc9.png' },
  { id: 12, name: 'Latin', accent: 'rose', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/d2518436b88a9094dfba1f569f532beadcfb0ef45c5438fde3cc66876dded456.png' },
  { id: 7, name: 'Electronic', accent: 'cyan', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/9f6f635b69a30efbfbc49ad88de10608f0304ba507564178f15c0264f2309e28.png' },
  { id: 21, name: 'Rock', accent: 'red', image: 'https://static.prod-images.emergentagent.com/jobs/db348711-c33f-411a-ab4f-4b2b6c5b3e49/images/5db1df611a38a82bae5f9c1de91d3f55389d7077ac1b187b02384f7fa5a00826.png' },
  { id: 11, name: 'Jazz', accent: 'amber', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/6b93f5196532195ba05ab6bf0c9ee4d159a82bbf3d280e8b98f16d7e8a877c5c.png' },
  { id: 51, name: 'K-Pop', accent: 'violet', image: 'https://static.prod-images.emergentagent.com/jobs/db348711-c33f-411a-ab4f-4b2b6c5b3e49/images/7a3b24bbd973ecbb1ebd6213de4830a6c92d624ec28324364d31bcdeb5144126.png' },
  { id: 6, name: 'Country', accent: 'yellow', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/e013166ac9b934f5830730a575b8cb4c748574c547e8b50a96cd5156676d5247.png' },
  { id: 24, name: 'Reggae', accent: 'green', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/d0a5c8d455d9caea42afaddbdf3d29b4b55afe52cbebdf4064545a8070d568de.png' },
  { id: 5, name: 'Classical', accent: 'slate', image: 'https://static.prod-images.emergentagent.com/jobs/a569541a-464b-447f-8f9c-d99a37f3c365/images/35d2e19c14d0cc004066e9331ceff79a357bffc84e5f619fa88c9a1e96f92d92.png' },
];

// Accent color mappings for icons - both dark and light mode
const accentColors = {
  purple: { 
    icon: 'text-purple-500', 
    iconLight: 'text-purple-600',
    bg: 'bg-purple-500/20', 
    bgLight: 'bg-purple-100',
    border: 'border-purple-500/30',
    borderLight: 'border-purple-200'
  },
  orange: { 
    icon: 'text-orange-500', 
    iconLight: 'text-orange-600',
    bg: 'bg-orange-500/20', 
    bgLight: 'bg-orange-100',
    border: 'border-orange-500/30',
    borderLight: 'border-orange-200'
  },
  pink: { 
    icon: 'text-pink-500', 
    iconLight: 'text-pink-600',
    bg: 'bg-pink-500/20', 
    bgLight: 'bg-pink-100',
    border: 'border-pink-500/30',
    borderLight: 'border-pink-200'
  },
  red: { 
    icon: 'text-red-500', 
    iconLight: 'text-red-600',
    bg: 'bg-red-500/20', 
    bgLight: 'bg-red-100',
    border: 'border-red-500/30',
    borderLight: 'border-red-200'
  },
  cyan: { 
    icon: 'text-cyan-500', 
    iconLight: 'text-cyan-600',
    bg: 'bg-cyan-500/20', 
    bgLight: 'bg-cyan-100',
    border: 'border-cyan-500/30',
    borderLight: 'border-cyan-200'
  },
  amber: { 
    icon: 'text-amber-500', 
    iconLight: 'text-amber-600',
    bg: 'bg-amber-500/20', 
    bgLight: 'bg-amber-100',
    border: 'border-amber-500/30',
    borderLight: 'border-amber-200'
  },
  yellow: { 
    icon: 'text-yellow-500', 
    iconLight: 'text-yellow-600',
    bg: 'bg-yellow-500/20', 
    bgLight: 'bg-yellow-100',
    border: 'border-yellow-500/30',
    borderLight: 'border-yellow-200'
  },
  rose: { 
    icon: 'text-rose-500', 
    iconLight: 'text-rose-600',
    bg: 'bg-rose-500/20', 
    bgLight: 'bg-rose-100',
    border: 'border-rose-500/30',
    borderLight: 'border-rose-200'
  },
  slate: { 
    icon: 'text-slate-500', 
    iconLight: 'text-slate-600',
    bg: 'bg-slate-500/20', 
    bgLight: 'bg-slate-100',
    border: 'border-slate-500/30',
    borderLight: 'border-slate-200'
  },
  violet: { 
    icon: 'text-violet-500', 
    iconLight: 'text-violet-600',
    bg: 'bg-violet-500/20', 
    bgLight: 'bg-violet-100',
    border: 'border-violet-500/30',
    borderLight: 'border-violet-200'
  },
  green: { 
    icon: 'text-green-500', 
    iconLight: 'text-green-600',
    bg: 'bg-green-500/20', 
    bgLight: 'bg-green-100',
    border: 'border-green-500/30',
    borderLight: 'border-green-200'
  },
  emerald: { 
    icon: 'text-emerald-500', 
    iconLight: 'text-emerald-600',
    bg: 'bg-emerald-500/20', 
    bgLight: 'bg-emerald-100',
    border: 'border-emerald-500/30',
    borderLight: 'border-emerald-200'
  },
};

const MusicSearchModal = ({ isOpen, onClose, onAddSong, session, queue, currentUserId, isHost, currentUsername }) => {
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [addedSongs, setAddedSongs] = useState(new Set());
  const [savedSessions, setSavedSessions] = useState([]);
  const [savedSessionsLoading, setSavedSessionsLoading] = useState(false);
  const [savedSessionsLoaded, setSavedSessionsLoaded] = useState(false);
  const [trendingSongs, setTrendingSongs] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genreSongs, setGenreSongs] = useState([]);
  const [genreLoading, setGenreLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionQueueState, setSessionQueueState] = useState([]);
  const [sessionQueueLoading, setSessionQueueLoading] = useState(false);
  const [view, setView] = useState('main');
  const [limitReachedMessage, setLimitReachedMessage] = useState(null);
  const [showLimitPopup, setShowLimitPopup] = useState(false); // New popup state
  const recognitionRef = useRef(null);

  const { isConfigured, isAuthorized, authorize } = useMusicKit();
  
  // Calculate how many songs the current guest has added to the queue
  const guestSongCount = React.useMemo(() => {
    if (isHost || !currentUserId) return 0;
    // Double-check: also treat user as host if they match session host
    const sessionHostId = session?.host_id || session?.host?.id;
    if (sessionHostId && sessionHostId === currentUserId) return 0;
    // Use the queue prop passed from SessionView
    const currentQueue = queue || [];
    console.log('[MusicSearchModal] Calculating guest song count:', {
      isHost,
      currentUserId,
      queueLength: currentQueue.length,
      queue: currentQueue.map(s => ({ title: s.title, added_by: s.added_by }))
    });
    const count = currentQueue.filter(song => 
      song.added_by?.id === currentUserId || 
      song.added_by?.user_id === currentUserId ||
      song.added_by === currentUserId
    ).length;
    console.log('[MusicSearchModal] Guest song count:', count);
    return count;
  }, [queue, currentUserId, isHost]);
  
  // Get the song limit - default to 5 if not set (matching backend default)
  // 0 means unlimited. If no session context (e.g. playlist), don't enforce limits.
  const songLimit = session ? (session.song_limit_per_guest !== undefined ? session.song_limit_per_guest : 5) : 0;
  
  // Additional host check using session data directly - check multiple fields
  const isActuallyHost = isHost || (currentUserId && (
    session?.host_id === currentUserId || 
    session?.host?.id === currentUserId ||
    session?.host?.user_id === currentUserId
  )) || (currentUsername && (
    session?.host?.username === currentUsername ||
    session?.host_username === currentUsername
  ));
  
  // Check if guest has reached their limit (0 means unlimited) — never applies to host
  const hasReachedLimit = !isHost && !isActuallyHost && songLimit > 0 && guestSongCount >= songLimit;
  
  console.log('[MusicSearchModal] Session data:', {
    sessionId: session?.id,
    song_limit_per_guest: session?.song_limit_per_guest,
    songLimit,
    fullSession: session
  });
  
  console.log('[MusicSearchModal] Limit check:', {
    isHost,
    songLimit,
    guestSongCount,
    hasReachedLimit,
    currentUserId
  });

  // Initialize voice recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        searchMusic(transcript);
        setIsListening(false);
        setView('search');
      };
      
      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
    
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
    };
  }, []);

  // Fetch saved sessions and trending on mount - OPTIMIZED for speed
  useEffect(() => {
    if (isOpen) {
      // Start fetching immediately in parallel - don't block UI
      // Genres are static so they display instantly
      if (!savedSessionsLoaded) {
        // Use a shorter timeout and show UI immediately
        fetchSavedSessions();
      }
      // Fetch trending in background
      fetchTrendingSongs();
    }
  }, [isOpen, savedSessionsLoaded]);

  const fetchSavedSessions = async () => {
    setSavedSessionsLoading(true);
    try {
      const token = localStorage.getItem('tapInToken');
      // Add timeout to prevent long waits
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch(`${API_URL}/api/sessions/my-sessions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        const saved = data.filter(s => s.is_saved);
        setSavedSessions(saved.slice(0, 10));
        setSavedSessionsLoaded(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to fetch saved sessions:', err);
      }
      // On error/timeout, just mark as loaded to show empty state
      setSavedSessionsLoaded(true);
    } finally {
      setSavedSessionsLoading(false);
    }
  };

  const fetchTrendingSongs = async () => {
    try {
      const token = localStorage.getItem('tapInToken');
      const response = await fetch(`${API_URL}/api/songs/trending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrendingSongs(data.slice(0, 10));
      }
    } catch (err) {
      console.error('Failed to fetch trending songs:', err);
      setTrendingSongs([]);
    }
  };

  const fetchGenreSongs = async (genreId, genreName) => {
    setGenreLoading(true);
    setSelectedGenre({ id: genreId, name: genreName });
    setView('genre');
    
    try {
      const response = await fetch(
        `${API_URL}/api/music/charts?genre=${genreId}&limit=20`
      );
      if (response.ok) {
        const data = await response.json();
        setGenreSongs(data.results || []);
      }
    } catch (err) {
      console.error('Failed to fetch genre songs:', err);
      setGenreSongs([]);
    } finally {
      setGenreLoading(false);
    }
  };

  const fetchSessionQueue = async (session) => {
    setSessionQueueLoading(true);
    setSelectedSession(session);
    setView('session');
    
    try {
      const token = localStorage.getItem('tapInToken');
      const response = await fetch(`${API_URL}/api/sessions/${session.id}/queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessionQueueState(data.queue || []);
      }
    } catch (err) {
      console.error('Failed to fetch session queue:', err);
      setSessionQueueState([]);
    } finally {
      setSessionQueueLoading(false);
    }
  };

  const handleVoiceSearch = () => {
    if (!recognitionRef.current) {
      alert('Voice search is not supported in your browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const searchMusic = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${API_URL}/api/music/search?term=${encodeURIComponent(searchTerm)}&types=songs&limit=15`
        );
        
        if (!response.ok) throw new Error('Search failed');

        const data = await response.json();
        setResults(data.results || []);
      } catch (err) {
        setError('Failed to search. Please try again.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250),
    []
  );

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length >= 2) {
      setView('search');
      searchMusic(value);
    } else if (value.length === 0) {
      setView('main');
      setResults([]);
    }
  };

  const handleAddSong = async (song) => {
    console.log('[MusicSearchModal] handleAddSong called:', {
      songTitle: song.title || song.name,
      hasReachedLimit,
      guestSongCount,
      songLimit,
      isHost
    });
    
    // CRITICAL: Check if already added (prevents double-tap)
    if (addedSongs.has(song.id)) {
      console.log('[MusicSearchModal] Song already added, ignoring duplicate tap');
      return;
    }
    
    // Check if guest has reached their song limit
    if (hasReachedLimit) {
      console.log('[MusicSearchModal] BLOCKING - Guest has reached limit');
      setShowLimitPopup(true); // Show popup modal
      return;
    }
    
    console.log('[MusicSearchModal] Adding song - limit not reached');
    // Mark as added IMMEDIATELY for instant feedback and double-tap prevention
    setAddedSongs(prev => new Set([...prev, song.id]));
    
    // Then notify parent - include genre for analytics
    // Use setTimeout to ensure state update completes first
    setTimeout(() => {
      onAddSong({
        id: song.id,
        title: song.name || song.title,
        artist: song.artist_name || song.artist,
        album: song.album_name || song.album,
        artwork_url: song.artwork_url,
        preview_url: song.preview_url,
        duration_ms: song.duration_ms,
        genre: song.genre || '', // Include genre for event analytics
      });
    }, 0);
  };
  
  const handleClose = () => {
    setAddedSongs(new Set());
    setQuery('');
    setResults([]);
    setView('main');
    setSelectedGenre(null);
    setSelectedSession(null);
    onClose();
  };

  const handleBack = () => {
    setView('main');
    setSelectedGenre(null);
    setSelectedSession(null);
    setGenreSongs([]);
    setSessionQueueState([]);
    setQuery('');
    setResults([]);
  };

  if (!isOpen) return null;

  // Song card component
  const SongCard = ({ song, showFireCount = false }) => {
    const isAdded = addedSongs.has(song.id);
    const songName = song.name || song.title;
    const artistName = song.artist_name || song.artist;
    
    return (
      <button
        onClick={() => handleAddSong(song)}
        disabled={isAdded}
        data-testid={`song-card-${song.id}`}
        className={`w-full flex items-center gap-3 p-3 rounded-2xl transition-all text-left group ${
          isAdded 
            ? (isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200') 
            : (isDarkMode ? 'hover:bg-white/5 active:bg-white/10 border border-transparent hover:border-white/10' : 'hover:bg-gray-50 active:bg-gray-100 border border-transparent hover:border-gray-200')
        }`}
      >
        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
          {song.artwork_url ? (
            <img src={song.artwork_url} alt={songName} className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
              <Music className={`w-6 h-6 ${isDarkMode ? 'text-white/50' : 'text-gray-400'}`} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold truncate ${isAdded ? 'text-green-500' : (isDarkMode ? 'text-white' : 'text-gray-900')}`}>{songName}</p>
          <p className={`text-sm truncate ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{artistName}</p>
          {showFireCount && song.fire_count > 0 && (
            <p className="text-xs text-orange-400 flex items-center gap-1 mt-0.5">
              <Flame className="w-3 h-3" /> {song.fire_count}
            </p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
          isAdded 
            ? 'bg-green-500 shadow-lg shadow-green-500/30' 
            : (isDarkMode ? 'bg-white/5 group-hover:bg-purple-500/20' : 'bg-gray-100 group-hover:bg-purple-100')
        }`}>
          {isAdded ? (
            <Check className="w-5 h-5 text-white" />
          ) : (
            <Plus className={`w-5 h-5 ${isDarkMode ? 'text-white/70 group-hover:text-purple-400' : 'text-gray-500 group-hover:text-purple-600'}`} />
          )}
        </div>
      </button>
    );
  };

  // Genre list item component - with image thumbnails, mobile responsive
  const GenreListItem = ({ genre, index }) => {
    const colors = accentColors[genre.accent] || accentColors.purple;
    
    return (
      <button
        onClick={() => fetchGenreSongs(genre.id, genre.name)}
        data-testid={`genre-${genre.name.toLowerCase()}`}
        className={`w-full flex items-center gap-3 p-2.5 rounded-xl group border transition-all duration-200 active:scale-[0.98] ${
          isDarkMode 
            ? `hover:bg-white/5 active:bg-white/10 border-transparent` 
            : `hover:bg-gray-50 active:bg-gray-100 border-transparent hover:border-gray-100`
        }`}
      >
        {/* Genre Image */}
        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-md ${
          isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
        }`}>
          <img 
            src={genre.image} 
            alt={genre.name}
            className="w-full h-full object-cover"
            loading="eager"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <div className="flex-1 text-left min-w-0">
          <span className={`text-sm sm:text-base font-semibold block truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{genre.name}</span>
          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Top charts</p>
        </div>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
          isDarkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-gray-100 group-hover:bg-gray-200'
        }`}>
          <Play className={`w-4 h-4 ${isDarkMode ? 'text-white/50 group-hover:text-white/70' : 'text-gray-500 group-hover:text-gray-700'}`} />
        </div>
      </button>
    );
  };

  // Session list item component with creative design
  const SessionListItem = ({ session, index }) => {
    // Use queue_length from API response (accurate song count)
    const songCount = session.queue_length || 0;
    
    return (
      <button
        onClick={() => fetchSessionQueue(session)}
        data-testid={`session-${session.id}`}
        className={`w-full flex items-center gap-4 p-3 rounded-2xl transition-all group border ${
          isDarkMode 
            ? 'hover:bg-purple-500/10 border-purple-500/0 hover:border-purple-500/20' 
            : 'hover:bg-purple-50 border-purple-100/0 hover:border-purple-200'
        }`}
        style={{ animationDelay: `${index * 30}ms` }}
      >
        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 shadow-lg relative">
          {session.cover_image ? (
            <img src={session.cover_image} alt={session.title} className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${isDarkMode ? 'from-purple-600 to-blue-600' : 'from-purple-400 to-blue-400'}`}>
              <Bookmark className="w-6 h-6 text-white/80" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{session.title}</p>
          <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
            {songCount} {songCount === 1 ? 'song' : 'songs'}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
          isDarkMode ? 'bg-white/5 group-hover:bg-purple-500/20' : 'bg-gray-100 group-hover:bg-purple-100'
        }`}>
          <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-white/50 group-hover:text-purple-400' : 'text-gray-500 group-hover:text-purple-600'}`} />
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50">
      <div 
        className={`w-full max-w-md rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col overflow-hidden border ${
          isDarkMode 
            ? 'bg-slate-900 border-white/10' 
            : 'bg-white border-gray-200'
        }`}
      >
        {/* Header */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            {view !== 'main' ? (
              <button 
                onClick={handleBack}
                data-testid="back-button"
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <ArrowLeft className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`} />
              </button>
            ) : (
              <div className="w-10" />
            )}
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {view === 'genre' ? selectedGenre?.name : view === 'session' ? selectedSession?.title : 'Add Music'}
            </h2>
            <button 
              onClick={handleClose}
              data-testid="close-modal"
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <X className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`} />
            </button>
          </div>
          
          {/* Search Input */}
          <div className="relative">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`} />
            <input
              type="text"
              value={query}
              onChange={handleQueryChange}
              placeholder={isListening ? "Listening..." : "Search songs, artists..."}
              data-testid="search-input"
              className={`w-full pl-12 pr-16 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all ${
                isDarkMode 
                  ? 'bg-white/5 text-white placeholder-white/40 border border-white/10 focus:border-purple-500/50' 
                  : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-gray-200 focus:border-purple-300'
              }`}
              autoFocus
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {loading && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
              <button
                onClick={handleVoiceSearch}
                data-testid="voice-search"
                className={`p-2 rounded-lg transition-all ${
                  isListening 
                    ? 'bg-red-500 animate-pulse' 
                    : (isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200')
                }`}
              >
                <Mic className={`w-5 h-5 ${isListening ? 'text-white' : (isDarkMode ? 'text-white/40' : 'text-gray-400')}`} />
              </button>
            </div>
          </div>
          
          {/* Song Limit Info for Guests */}
          {!isHost && !isActuallyHost && songLimit > 0 && (
            <div className={`mt-3 flex items-center justify-between px-3 py-2 rounded-lg ${
              hasReachedLimit 
                ? (isDarkMode ? 'bg-red-500/20 border border-red-500/30' : 'bg-red-50 border border-red-200')
                : (isDarkMode ? 'bg-white/5' : 'bg-gray-50')
            }`}>
              <span className={`text-sm ${hasReachedLimit ? 'text-red-400' : (isDarkMode ? 'text-white/60' : 'text-gray-500')}`}>
                Songs added: {guestSongCount}/{songLimit}
              </span>
              {hasReachedLimit && (
                <span className="text-xs text-red-400 font-medium">Limit reached</span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* SEARCH RESULTS VIEW */}
          {view === 'search' && (
            <div className="p-4">
              {error && <div className="text-center text-red-500 py-4">{error}</div>}
              {!loading && results.length === 0 && query && (
                <div className={`text-center py-8 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  No results found for &ldquo;{query}&rdquo;
                </div>
              )}
              <div className="space-y-2">
                {results.map((song) => <SongCard key={song.id} song={song} />)}
              </div>
            </div>
          )}

          {/* GENRE VIEW */}
          {view === 'genre' && (
            <div className="p-4">
              {genreLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
              ) : genreSongs.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  No songs found in this genre
                </div>
              ) : (
                <div className="space-y-2">
                  {genreSongs.map((song) => <SongCard key={song.id} song={song} />)}
                </div>
              )}
            </div>
          )}

          {/* SESSION QUEUE VIEW */}
          {view === 'session' && (
            <div className="p-4">
              {sessionQueueLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
              ) : sessionQueueState.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  No songs in this session
                </div>
              ) : (
                <div className="space-y-2">
                  {sessionQueueState.map((song) => <SongCard key={song.id} song={song} />)}
                </div>
              )}
            </div>
          )}

          {/* MAIN VIEW */}
          {view === 'main' && (
            <div className="p-4 space-y-6">
              {/* Saved Sessions Section - Show skeleton while loading */}
              {(savedSessionsLoading || savedSessions.length > 0) && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Bookmark className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                      Add from Saved Sessions
                    </h3>
                  </div>
                  {savedSessionsLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className={`flex items-center gap-4 p-3 rounded-2xl animate-pulse ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <div className={`w-14 h-14 rounded-xl ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                          <div className="flex-1">
                            <div className={`h-4 w-24 rounded ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                            <div className={`h-3 w-16 rounded mt-2 ${isDarkMode ? 'bg-white/5' : 'bg-gray-150'}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {savedSessions.map((session, index) => (
                        <SessionListItem key={session.id} session={session} index={index} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Genre List */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Music className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                    Browse by Genre
                  </h3>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  {GENRES.map((genre, index) => (
                    <GenreListItem key={genre.id} genre={genre} index={index} />
                  ))}
                </div>
              </div>

              {/* Trending Songs */}
              {trendingSongs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                      Trending on Tap In
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {trendingSongs.slice(0, 5).map((song) => (
                      <SongCard key={song.id} song={song} showFireCount />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
          <p className={`text-xs flex items-center justify-center gap-1.5 ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`}>
            Powered by Apple Music
          </p>
        </div>
      </div>
      
      {/* Song Limit Reached Popup */}
      {showLimitPopup && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className={`mx-4 w-full max-w-sm rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}>
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Song Limit Reached
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                You've reached the maximum of {songLimit} {songLimit === 1 ? 'song' : 'songs'} allowed by the host.
              </p>
            </div>
            <div className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowLimitPopup(false)}
                className={`w-full py-4 text-center font-semibold ${
                  isDarkMode 
                    ? 'text-purple-400 hover:bg-gray-800' 
                    : 'text-purple-600 hover:bg-gray-50'
                } transition-colors`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(MusicSearchModal);
